import { msg } from '@lingui/core/macro';
import type { I18n, MessageDescriptor } from '@lingui/core';

import { NAV_ITEMS, navItemLabel } from '@/lib/navigation';
import { NAV_GROUPS } from '@/pages/settings/settingsNav';

/** Brand name; the suffix of every document title. */
export const APP_TITLE = 'Daycare Butler';

type TitleSource =
  | { title: MessageDescriptor }
  /** A sub-page: rendered "Section: Page", e.g. "Settings: Groups". */
  | { section: MessageDescriptor; page: MessageDescriptor }
  /** A redirect route takes the title of its target. */
  | { sameAs: string };

/** The sidebar label of a section, so a sub-page title starts with the name the sidebar shows. */
function sectionLabel(href: string): MessageDescriptor {
  const item = NAV_ITEMS.find((candidate) => candidate.href === href);
  if (!item) throw new Error(`routeTitles: no navigation item for "${href}"`);
  return navItemLabel(item);
}

const SETTINGS = msg({ id: 'settings.title', message: 'Settings' });
const DASHBOARD = sectionLabel('/');
const PROSPECTS = sectionLabel('/prospects');
const BILLING = sectionLabel('/billing');
const CAPACITY = sectionLabel('/capacity');

/**
 * Titles of the app-shell routes (`routes/appRoutes.tsx`, `pages/billing/routes.tsx`).
 * Section pages take the sidebar label and settings pages the settings rail
 * label, so the browser tab, the screen-reader announcement and the
 * navigation name a page the same way. A path without an entry inherits the
 * title of its longest listed prefix (`/children/:id/edit` reads "Children").
 */
const ROUTE_TITLE_SOURCES: ReadonlyMap<string, TitleSource> = new Map<string, TitleSource>([
  ...NAV_ITEMS.map((item): [string, TitleSource] => [item.href, { title: navItemLabel(item) }]),
  ['/dashboard/analytics', { section: DASHBOARD, page: msg({ id: 'cockpit.analytics', message: 'Analytics' }) }],
  ['/prospects/new', { title: msg({ id: 'routeTitles.prospectNew', message: 'New prospect' }) }],
  ['/prospects/board', { section: PROSPECTS, page: msg({ id: 'prospects.board.view.board', message: 'Board' }) }],
  ['/prospects/kpis', { section: PROSPECTS, page: msg({ id: 'prospects.kpis.view.kpis', message: 'Key figures' }) }],
  ['/children/new', { title: msg({ id: 'routeTitles.childNew', message: 'New child' }) }],
  ['/contacts/create', { title: msg({ id: 'routeTitles.contactNew', message: 'New contact' }) }],
  ['/employees/create', { title: msg({ id: 'routeTitles.employeeNew', message: 'New employee' }) }],
  ['/capacity/graduation', { section: CAPACITY, page: msg({ id: 'capacity.planMoves.title', message: 'Plan group changes' }) }],
  [
    '/contract-terminations',
    { title: msg({ id: 'termination.review.title', message: 'Review & Confirm termination' }) },
  ],
  ['/billing/invoices', { section: BILLING, page: msg({ id: 'billing.nav.invoices', message: 'Invoices' }) }],
  ['/billing/dunning', { section: BILLING, page: msg({ id: 'billing.nav.dunning', message: 'Dunning' }) }],
  ['/billing/email-cockpit', { section: BILLING, page: msg({ id: 'billing.emailCockpit.nav', message: 'Email Cockpit' }) }],
  [
    '/billing/reconciliation',
    { section: BILLING, page: msg({ id: 'billing.nav.reconciliation', message: 'Reconciliation' }) },
  ],
  ['/settings', { title: SETTINGS }],
  ['/guide', { title: msg({ id: 'guide.title', message: 'Guide' }) }],
  ['/whats-new', { title: msg({ id: 'whatsNew.title', message: "What's new" }) }],
  ...NAV_GROUPS.flatMap((group) =>
    group.items.map((item): [string, TitleSource] => [item.path, { section: SETTINGS, page: item.label }]),
  ),
  ['/enrolment', { sameAs: '/' }],
  ['/inspection', { sameAs: '/' }],
  ['/analytics', { sameAs: '/' }],
  ['/workforce', { sameAs: '/time-tracking' }],
  ['/menus', { sameAs: '/menu-activities' }],
  ['/weekly-activities', { sameAs: '/menu-activities' }],
  ['/admin/subsidy-offices', { sameAs: '/settings/subsidy-offices' }],
]);

/** The listed path that covers `pathname`: itself, else its longest listed prefix. `/` covers only itself. */
function coveringPath(pathname: string): string | undefined {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  let best: string | undefined;
  for (const path of ROUTE_TITLE_SOURCES.keys()) {
    const covers =
      normalized === path || (path !== '/' && normalized.startsWith(`${path}/`));
    if (covers && (best === undefined || path.length > best.length)) best = path;
  }
  return best;
}

/**
 * The localised page title of an app-shell path, `undefined` for a path no
 * route covers. Sub-pages read "Settings: Groups"; detail pages read as their
 * section and add the record name through `useDocumentTitle`.
 */
export function routeTitle(pathname: string, i18n: I18n): string | undefined {
  const path = coveringPath(pathname);
  if (path === undefined) return undefined;
  let source = ROUTE_TITLE_SOURCES.get(path);
  if (source && 'sameAs' in source) source = ROUTE_TITLE_SOURCES.get(source.sameAs);
  if (!source || 'sameAs' in source) return undefined;
  if ('title' in source) return i18n._(source.title);
  return i18n._({
    id: 'routeTitles.subPage',
    message: '{section}: {page}',
    values: { section: i18n._(source.section), page: i18n._(source.page) },
  });
}

/** "Part · Part · Daycare Butler", skipping empty parts. */
export function formatDocumentTitle(...parts: (string | undefined | null)[]): string {
  return [...parts.filter((part): part is string => !!part && part.trim() !== ''), APP_TITLE].join(' · ');
}
